import '../styles/TestPlayground.css';

import React, { useEffect, useRef, useState } from 'react';

import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { useNavigate, useParams } from 'react-router-dom';

import { Button, Card, Container, Typography } from './common';

type ResolutionKey = 'original' | '360p' | '480p' | '720p' | '1080p';
type QualityKey = 'smallest' | 'balanced' | 'high' | 'best';
type TranscodeStatus = 'idle' | 'loading-ffmpeg' | 'compressing' | 'done' | 'error';
type RecordQualityKey = 'low' | 'medium' | 'high' | 'max';
type RecorderStatus = 'idle' | 'requesting' | 'preview' | 'recording' | 'done' | 'error';

interface QualityOption {
  key: QualityKey;
  label: string;
  crf: number;
  preset: string;
}

const RESOLUTION_OPTIONS: { key: ResolutionKey; label: string }[] = [
  { key: 'original', label: 'Original' },
  { key: '360p', label: '360p' },
  { key: '480p', label: '480p' },
  { key: '720p', label: '720p' },
  { key: '1080p', label: '1080p' },
];

const RESOLUTION_HEIGHTS: Record<Exclude<ResolutionKey, 'original'>, number> = {
  '360p': 360,
  '480p': 480,
  '720p': 720,
  '1080p': 1080,
};

const QUALITY_OPTIONS: QualityOption[] = [
  { key: 'smallest', label: 'Smallest (CRF 35)', crf: 35, preset: 'veryfast' },
  { key: 'balanced', label: 'Balanced (CRF 28)', crf: 28, preset: 'fast' },
  { key: 'high', label: 'High quality (CRF 23)', crf: 23, preset: 'fast' },
  { key: 'best', label: 'Best quality (CRF 18)', crf: 18, preset: 'medium' },
];

const RECORD_QUALITY_OPTIONS: { key: RecordQualityKey; label: string; bps: number }[] = [
  { key: 'low', label: 'Low - 1 Mbps (~3.8 MB / 30 s)', bps: 1_000_000 },
  { key: 'medium', label: 'Medium - 2.5 Mbps (~9 MB / 30 s)', bps: 2_500_000 },
  { key: 'high', label: 'High - 5 Mbps (~19 MB / 30 s)', bps: 5_000_000 },
  { key: 'max', label: 'Max - 8 Mbps (~30 MB / 30 s)', bps: 8_000_000 },
];

const FFMPEG_CORE = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';

function fmtBytes(bytes: number): string {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtMs(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`;
  }
  return `${(ms / 1000).toFixed(1)}s`;
}

function getBestMimeType(): string {
  const candidates = [
    'video/mp4;codecs=h264,aac',
    'video/mp4',
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  return candidates.find((value) => MediaRecorder.isTypeSupported(value)) ?? '';
}

// ── Camera Modal ─────────────────────────────────────────────────────────────

interface CameraModalProps {
  quality: RecordQualityKey;
  onCapture: (blob: Blob, mimeType: string) => void;
  onClose: () => void;
}

function CameraModal({ quality, onCapture, onClose }: CameraModalProps): React.ReactElement {
  const [camStatus, setCamStatus] = useState<'requesting' | 'preview' | 'recording' | 'error'>('requesting');
  const [elapsedSec, setElapsedSec] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  const streamRef = useRef<MediaStream | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const liveVideoRef = useRef<HTMLVideoElement | null>(null);

  const stopStream = (): void => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const clearTimer = (): void => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const startCamera = async (facing: 'environment' | 'user'): Promise<void> => {
    setCamStatus('requesting');
    stopStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing },
        audio: true,
      });
      streamRef.current = stream;
      if (liveVideoRef.current) liveVideoRef.current.srcObject = stream;
      setCamStatus('preview');
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Camera access denied');
      setCamStatus('error');
    }
  };

  useEffect(() => {
    void startCamera('environment');
    return () => { clearTimer(); stopStream(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLiveVideoRef = (el: HTMLVideoElement | null): void => {
    liveVideoRef.current = el;
    if (el && streamRef.current) el.srcObject = streamRef.current;
  };

  const handleFlip = (): void => {
    const current = streamRef.current?.getVideoTracks()[0]?.getSettings().facingMode;
    void startCamera(current === 'environment' ? 'user' : 'environment');
  };

  const startRecording = (): void => {
    if (!streamRef.current) return;
    const mime = getBestMimeType();
    chunksRef.current = [];
    const qualityOpt = RECORD_QUALITY_OPTIONS.find(q => q.key === quality)!;
    const recorder = new MediaRecorder(streamRef.current, {
      mimeType: mime || undefined,
      videoBitsPerSecond: qualityOpt.bps,
    });
    recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    recorder.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: mime || 'video/webm' });
      stopStream();
      onCapture(blob, mime);
    };
    recorderRef.current = recorder;
    setElapsedSec(0);
    setCamStatus('recording');
    timerRef.current = setInterval(() => setElapsedSec(s => s + 1), 1000);

    // PORT NOTE — black-flash on recording start (iOS Safari + Chrome):
    // Calling recorder.start() synchronously causes the hardware encoder to
    // reconfigure mid-paint, producing 2–4 black frames in the live preview.
    // Deferring via rAF lets the current paint cycle finish (REC badge visible)
    // before the encoder kicks in, which eliminates the flash in testing.
    // Also intentionally no timeslice arg: recorder.start(100) flushes the
    // encoder every 100 ms and causes repeated pipeline stalls. Without a
    // timeslice, ondataavailable fires once on stop — fine for short clips.
    // If the flash returns on a specific device, the nuclear option is to
    // capture a freeze-frame canvas snapshot and overlay it for ~200 ms at
    // recording start to hide the glitch entirely.
    requestAnimationFrame(() => recorder.start());
  };

  const stopRecording = (): void => {
    clearTimer();
    recorderRef.current?.stop();
  };

  const handleClose = (): void => {
    clearTimer();
    recorderRef.current?.stop();
    stopStream();
    onClose();
  };

  const isRecording = camStatus === 'recording';
  const canRecord = camStatus === 'preview' || camStatus === 'recording';
  const timerStr = `${String(Math.floor(elapsedSec / 60)).padStart(2, '0')}:${String(elapsedSec % 60).padStart(2, '0')}`;

  return (
    <div className="cam-modal">
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video ref={handleLiveVideoRef} className="cam-modal-video" autoPlay muted playsInline />

      <div className="cam-modal-top">
        <button type="button" className="cam-modal-icon-btn" onClick={handleClose} aria-label="Close">
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20" aria-hidden="true">
            <path d="M18 6L6 18M6 6l12 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </button>

        {isRecording && (
          <span className="cam-modal-timer">
            <span className="cam-modal-rec-dot" aria-hidden="true" />
            {timerStr}
          </span>
        )}

        <button
          type="button"
          className="cam-modal-icon-btn"
          onClick={handleFlip}
          disabled={isRecording}
          aria-label="Flip camera"
        >
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20" aria-hidden="true">
            <path d="M1 4v6h6M23 20v-6h-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      {camStatus === 'error' && (
        <div className="cam-modal-error-overlay">
          <p className="cam-modal-error-msg">{errorMsg}</p>
          <button type="button" className="cam-modal-icon-btn" onClick={() => void startCamera('environment')} aria-label="Retry">
            Retry
          </button>
        </div>
      )}

      <div className="cam-modal-bottom">
        <div className="cam-modal-side" />
        <button
          type="button"
          className="cam-modal-shutter"
          onClick={isRecording ? stopRecording : startRecording}
          disabled={!canRecord}
          aria-label={isRecording ? 'Stop recording' : 'Start recording'}
        >
          <span className={`cam-modal-shutter-inner${isRecording ? ' cam-modal-shutter-inner--stop' : ''}`} />
        </button>
        <div className="cam-modal-side" />
      </div>
    </div>
  );
}

// ── TestPlayground ────────────────────────────────────────────────────────────

export default function TestPlayground(): React.ReactElement {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();

  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [recordQuality, setRecordQuality] = useState<RecordQualityKey>('medium');
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [recordedBytes, setRecordedBytes] = useState(0);
  const [recordedDuration, setRecordedDuration] = useState<number | null>(null);
  const [recordMimeType, setRecordMimeType] = useState('');
  const prevRecordedUrlRef = useRef<string | null>(null);

  const [inputFile, setInputFile] = useState<File | null>(null);
  const [outputUrl, setOutputUrl] = useState<string | null>(null);
  const [resolution, setResolution] = useState<ResolutionKey>('720p');
  const [compressionQuality, setCompressionQuality] = useState<QualityKey>('balanced');
  const [transcodeStatus, setTranscodeStatus] = useState<TranscodeStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [inputBytes, setInputBytes] = useState(0);
  const [outputBytes, setOutputBytes] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [transcodeError, setTranscodeError] = useState('');
  const [outputDuration, setOutputDuration] = useState<number | null>(null);

  const ffmpegRef = useRef<FFmpeg | null>(null);
  const prevOutputUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const handleCapture = (blob: Blob, mime: string): void => {
    if (prevRecordedUrlRef.current) URL.revokeObjectURL(prevRecordedUrlRef.current);
    const url = URL.createObjectURL(blob);
    prevRecordedUrlRef.current = url;
    setRecordedUrl(url);
    setRecordedBytes(blob.size);
    setRecordMimeType(mime);
    setRecordedDuration(null);
    setCameraModalOpen(false);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (prevOutputUrlRef.current) {
      URL.revokeObjectURL(prevOutputUrlRef.current);
      prevOutputUrlRef.current = null;
    }

    setInputFile(file);
    setInputBytes(file.size);
    setOutputUrl(null);
    setOutputBytes(0);
    setTranscodeStatus('idle');
    setProgress(0);
    setTranscodeError('');
    setOutputDuration(null);
    event.target.value = '';
  };

  const handleTranscode = async (): Promise<void> => {
    if (!inputFile) {
      return;
    }

    const startTime = Date.now();

    try {
      if (!ffmpegRef.current) {
        setTranscodeStatus('loading-ffmpeg');
        const ffmpeg = new FFmpeg();
        ffmpeg.on('progress', ({ progress: value }) => {
          setProgress(Math.min(99, Math.round(value * 100)));
        });

        await ffmpeg.load({
          coreURL: await toBlobURL(`${FFMPEG_CORE}/ffmpeg-core.js`, 'text/javascript'),
          wasmURL: await toBlobURL(`${FFMPEG_CORE}/ffmpeg-core.wasm`, 'application/wasm'),
        });

        ffmpegRef.current = ffmpeg;
      }

      setTranscodeStatus('compressing');
      setProgress(0);
      const ffmpeg = ffmpegRef.current;
      await ffmpeg.writeFile('input.mp4', await fetchFile(inputFile));

      const args: string[] = ['-i', 'input.mp4', '-y'];
      if (resolution !== 'original') {
        const height = RESOLUTION_HEIGHTS[resolution as Exclude<ResolutionKey, 'original'>];
        args.push('-vf', `scale=-2:${height}`);
      }

      const qualityOption = QUALITY_OPTIONS.find((option) => option.key === compressionQuality);
      if (!qualityOption) {
        throw new Error(`Unknown quality: ${compressionQuality}`);
      }

      args.push(
        '-c:v', 'libx264',
        '-crf', String(qualityOption.crf),
        '-preset', qualityOption.preset,
        '-c:a', 'aac',
        '-b:a', '128k',
        '-movflags', '+faststart',
        'output.mp4',
      );

      await ffmpeg.exec(args);
      const data = await ffmpeg.readFile('output.mp4');
      if (typeof data === 'string') {
        throw new Error('Unexpected string output from ffmpeg');
      }

      const bytes = new Uint8Array(data.byteLength);
      bytes.set(data);
      const blob = new Blob([bytes.buffer], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);

      if (prevOutputUrlRef.current) {
        URL.revokeObjectURL(prevOutputUrlRef.current);
      }
      prevOutputUrlRef.current = url;

      setOutputUrl(url);
      setOutputBytes(blob.size);
      setElapsedMs(Date.now() - startTime);
      setProgress(100);
      setTranscodeStatus('done');

      await ffmpeg.deleteFile('input.mp4').catch(() => undefined);
      await ffmpeg.deleteFile('output.mp4').catch(() => undefined);
    } catch (error) {
      setTranscodeError(error instanceof Error ? error.message : String(error));
      setTranscodeStatus('error');
    }
  };

  const transcodeBusy = transcodeStatus === 'loading-ffmpeg' || transcodeStatus === 'compressing';
  const outputCompression = outputBytes && inputBytes
    ? Math.round((1 - outputBytes / inputBytes) * 100)
    : null;

  const outputBitrateKbps = outputDuration && outputBytes && outputDuration > 0
    ? Math.round((outputBytes * 8) / outputDuration / 1000)
    : null;

  return (
    <Container>
      <Card className="test-playground-card">
        <Typography variant="h2">Test Playground</Typography>
        <Typography variant="body">
          Camera tools require HTTPS. This works on Firebase Hosting URLs, not local HTTP.
        </Typography>

        <div className="test-playground-actions">
          <Button variant="secondary" onClick={() => navigate(`/session/${sessionId}`)}>
            Back to Dashboard
          </Button>
        </div>

        <div className="test-playground-section">
          <Typography variant="h3">Camera Recorder</Typography>
          <label className="test-playground-label" htmlFor="record-quality">Quality</label>
          <select
            id="record-quality"
            className="test-playground-select"
            value={recordQuality}
            onChange={(event) => setRecordQuality(event.target.value as RecordQualityKey)}
          >
            {RECORD_QUALITY_OPTIONS.map((option) => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>

          <Button onClick={() => setCameraModalOpen(true)}>Open Camera</Button>

          {recordedUrl && (
            <div className="test-playground-result">
              <Typography variant="body">Size: {fmtBytes(recordedBytes)}</Typography>
              {recordedDuration !== null && (
                <Typography variant="body">Duration: {recordedDuration.toFixed(1)}s</Typography>
              )}
              {recordedDuration && recordedDuration > 0 && (
                <Typography variant="body">
                  Actual bitrate: {((recordedBytes * 8) / recordedDuration / 1_000_000).toFixed(1)} Mbps
                </Typography>
              )}
              <Typography variant="body">Format: {recordMimeType.split(';')[0] || '—'}</Typography>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                className="test-playground-video"
                src={recordedUrl}
                controls
                playsInline
                onLoadedMetadata={(e) => setRecordedDuration((e.target as HTMLVideoElement).duration)}
              />
              <div className="test-playground-inline-actions">
                <a href={recordedUrl} download={`recorded.${recordMimeType.includes('mp4') ? 'mp4' : 'webm'}`} className="test-playground-download">
                  Download
                </a>
                <Button variant="secondary" onClick={() => { setRecordedUrl(null); setRecordedBytes(0); setRecordedDuration(null); }}>
                  Record Again
                </Button>
              </div>
            </div>
          )}
        </div>

        {cameraModalOpen && (
          <CameraModal
            quality={recordQuality}
            onCapture={handleCapture}
            onClose={() => setCameraModalOpen(false)}
          />
        )}

        <div className="test-playground-section">
          <Typography variant="h3">Video Compression</Typography>
          <div className="test-playground-inline-actions">
            <Button onClick={() => cameraInputRef.current?.click()} disabled={transcodeBusy}>
              Record Video
            </Button>
            <Button variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={transcodeBusy}>
              {inputFile ? inputFile.name : 'Choose Video File'}
            </Button>
            {inputFile && <Typography variant="body">{fmtBytes(inputBytes)}</Typography>}
          </div>

          <input
            ref={cameraInputRef}
            type="file"
            accept="video/*"
            capture="environment"
            className="test-playground-hidden-input"
            onChange={handleFileChange}
            aria-label="Record a video to compress"
          />
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            className="test-playground-hidden-input"
            onChange={handleFileChange}
            aria-label="Choose a video to compress"
          />

          <label className="test-playground-label" htmlFor="resolution-select">Resolution</label>
          <select
            id="resolution-select"
            className="test-playground-select"
            value={resolution}
            onChange={(event) => setResolution(event.target.value as ResolutionKey)}
            disabled={transcodeBusy}
          >
            {RESOLUTION_OPTIONS.map((option) => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>

          <label className="test-playground-label" htmlFor="compression-quality-select">Compression Quality</label>
          <select
            id="compression-quality-select"
            className="test-playground-select"
            value={compressionQuality}
            onChange={(event) => setCompressionQuality(event.target.value as QualityKey)}
            disabled={transcodeBusy}
          >
            {QUALITY_OPTIONS.map((option) => (
              <option key={option.key} value={option.key}>{option.label}</option>
            ))}
          </select>

          <Button
            onClick={() => { void handleTranscode(); }}
            disabled={!inputFile || transcodeBusy}
          >
            {transcodeStatus === 'loading-ffmpeg'
              ? 'Loading FFmpeg...'
              : transcodeStatus === 'compressing'
                ? 'Compressing...'
                : 'Compress'}
          </Button>

          {transcodeStatus === 'loading-ffmpeg' && (
            <Typography variant="body">
              Downloading FFmpeg engine (~30 MB). This usually takes 10-20 seconds on first load.
            </Typography>
          )}

          {transcodeStatus === 'compressing' && (
            <Typography variant="body">Progress: {progress}%</Typography>
          )}

          {transcodeStatus === 'error' && (
            <div className="error-message">{transcodeError}</div>
          )}

          {transcodeStatus === 'done' && outputUrl && (
            <div className="test-playground-result">
              <Typography variant="body">Input: {fmtBytes(inputBytes)}</Typography>
              <Typography variant="body">Output: {fmtBytes(outputBytes)}</Typography>
              {outputCompression !== null && (
                <Typography variant="body">
                  Saved: {outputCompression > 0 ? `-${outputCompression}%` : `+${Math.abs(outputCompression)}%`}
                </Typography>
              )}
              <Typography variant="body">Time: {fmtMs(elapsedMs)}</Typography>
              {outputBitrateKbps !== null && (
                <Typography variant="body">
                  Bitrate: {outputBitrateKbps >= 1000
                    ? `${(outputBitrateKbps / 1000).toFixed(1)} Mbps`
                    : `${outputBitrateKbps} kbps`}
                </Typography>
              )}
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <video
                className="test-playground-video"
                src={outputUrl}
                controls
                playsInline
                onLoadedMetadata={(event) => {
                  const target = event.target as HTMLVideoElement;
                  setOutputDuration(target.duration);
                }}
              />
              <a href={outputUrl} download="compressed.mp4" className="test-playground-download">
                Download compressed.mp4
              </a>
            </div>
          )}
        </div>
      </Card>
    </Container>
  );
}
